import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navigation from '../components/shared/Navigation';
import Footer from '../components/shared/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Bed, Bath, Maximize } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function UnitDetailPage() {
  const { unitId } = useParams();
  const [unit, setUnit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    fetch(`${API_URL}/api/units/${unitId}`)
      .then(res => res.json())
      .then(data => {
        setUnit(data);
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        setLoading(false);
      });
  }, [unitId]);

  if (loading) {
    return (
      <div>
        <Navigation />
        <div className="container section-spacing text-center">Loading...</div>
        <Footer />
      </div>
    );
  }

  if (!unit) {
    return (
      <div>
        <Navigation />
        <div className="container section-spacing text-center">Unit not found</div>
        <Footer />
      </div>
    );
  }

  return (
    <div>
      <Navigation />
      
      <div className="container section-spacing">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Gallery */}
          <div className="lg:col-span-2 space-y-4">
            <div className="aspect-[16/10] overflow-hidden rounded-xl">
              <img
                src={unit.images[selectedImage]}
                alt={`${unit.unit_number} view ${selectedImage + 1}`}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              {unit.images.slice(0, 3).map((img, idx) => (
                <button
                  key={idx}
                  onClick={() => setSelectedImage(idx)}
                  className={`aspect-[16/10] overflow-hidden rounded-lg border-2 ${
                    selectedImage === idx ? 'border-[#A06A3B]' : 'border-transparent'
                  }`}
                >
                  <img src={img} alt={`Thumbnail ${idx + 1}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>

          {/* Details & CTA */}
          <div className="lg:sticky lg:top-24 h-fit">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="font-[var(--font-display)] text-2xl">{unit.unit_number}</CardTitle>
                  <Badge variant={unit.status === 'available' ? 'default' : 'secondary'}>
                    {unit.status}
                  </Badge>
                </div>
                <div className="text-3xl font-[var(--font-display)] text-[#A06A3B] mt-2">
                  ${unit.price.toLocaleString()}{unit.type === 'rental' ? '/month' : ''}
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <Bed className="h-6 w-6 mx-auto mb-2 text-muted-foreground" />
                    <div className="text-sm font-medium">{unit.bedrooms} Bed</div>
                  </div>
                  <div>
                    <Bath className="h-6 w-6 mx-auto mb-2 text-muted-foreground" />
                    <div className="text-sm font-medium">{unit.bathrooms} Bath</div>
                  </div>
                  <div>
                    <Maximize className="h-6 w-6 mx-auto mb-2 text-muted-foreground" />
                    <div className="text-sm font-medium">{unit.square_feet} sqft</div>
                  </div>
                </div>

                <div>
                  <h3 className="font-semibold mb-2">Features</h3>
                  <div className="flex flex-wrap gap-2">
                    {unit.features.map((feature, idx) => (
                      <Badge key={idx} variant="outline">{feature}</Badge>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className="font-semibold mb-2">Description</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{unit.description}</p>
                </div>

                {unit.status === 'available' && (
                  <div className="space-y-4">
                    {/* How to Apply Instructions */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h4 className="font-semibold mb-2 text-blue-900">
                        📝 {unit.type === 'rental' ? 'How to Apply:' : 'How to Purchase:'}
                      </h4>
                      {unit.type === 'rental' ? (
                        <>
                          <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside mb-3">
                            <li>Click "Apply Now" below</li>
                            <li>Fill out application form</li>
                            <li>Pay $100 application fee</li>
                            <li>We'll review and contact you within 24 hours</li>
                          </ol>
                          <p className="text-xs text-blue-700 bg-blue-100 p-2 rounded">
                            <strong>💡 Moving in with family or roommates?</strong> You can add additional occupants on the application form!
                          </p>
                        </>
                      ) : (
                        <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                          <li>Click "Inquire Now" below</li>
                          <li>Submit your information</li>
                          <li>Our team will contact you to discuss next steps</li>
                          <li>Schedule a tour and review purchase details</li>
                        </ol>
                      )}
                    </div>

                    {/* Action Buttons */}
                    <div className="space-y-2">
                      {unit.type === 'rental' ? (
                        <>
                          <Button
                            data-testid="unit-detail-apply-button"
                            className="w-full bg-[#A06A3B] text-[#FBF8F2] hover:bg-[#6F4526] shadow-[0_14px_30px_rgba(160,106,59,0.22)] rounded-[var(--btn-radius)]"
                            onClick={() => navigate(`/apply/${unit.id}`)}
                          >
                            Apply Now ($100 Fee)
                          </Button>
                          <Button
                            data-testid="unit-detail-reserve-button"
                            variant="outline"
                            className="w-full rounded-[var(--btn-radius)]"
                            onClick={() => navigate(`/apply/${unit.id}?step=deposit`)}
                          >
                            Reserve with $250 Deposit
                          </Button>
                        </>
                      ) : (
                        <Button
                          data-testid="unit-detail-inquire-button"
                          className="w-full bg-[#A06A3B] text-[#FBF8F2] hover:bg-[#6F4526] shadow-[0_14px_30px_rgba(160,106,59,0.22)] rounded-[var(--btn-radius)]"
                          onClick={() => navigate('/contact', { state: { unitId: unit.id } })}
                        >
                          Inquire Now
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}